import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import '../scss/Header.scss';
import { Select, SelectOption } from 'appkit-react';
import { BENCHMARK } from './utils/Constants';

const useCloseOnOutsideClick = (containerRef, onClose) => {
    useEffect(() => {
        const handleOutsideClick = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [containerRef, onClose]);
};

const getDefaultBenchmarkQuarter = (startQuarterList, endQuarterList) => {
    if (endQuarterList.length) {
        return endQuarterList[endQuarterList.length - 1];
    }
    if (startQuarterList.length) {
        return startQuarterList[startQuarterList.length - 1];
    }
    return '';
};

const TITLE_LABEL = "Generate RSSID Narrative";
const GROUP_TITLE = "INSTITUTIONS";
const PLACEHOLDER = "Search RSSD IDs or Name";
const EMPTY_TEXT = "Click to select RSSD IDs";
const EMPTY_TEXT_TEXT = "Click to select RSSD";

const RssdDropdown = ({
    id,
    labelId,
    options = [],
    value,
    onChange,
    placeholder = { PLACEHOLDER },
    groupTitle = { GROUP_TITLE },
    emptyText = { EMPTY_TEXT_TEXT },
    isMulti = false,
    lockedValues = [],
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const dropdownRef = useRef(null);
    const lockedSet = useMemo(() => new Set(lockedValues.filter(Boolean)), [lockedValues]);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
    }, []);

    useCloseOnOutsideClick(dropdownRef, closeDropdown);

    const toggleDropdown = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const handleSearchChange = useCallback(e => {
        setSearchText(e.target.value);
    }, []);

    const ariaLabelledBy = useMemo(() => (labelId ? `${labelId} ${id}` : undefined), [id, labelId]);

    const rssdValueToLabelMap = useMemo(() => {
        const map = new Map();
        options.forEach(opt => {
            map.set(opt.value, opt.label);
        });
        return map;
    }, [options]);

    const multiSelectedValues = useMemo(
        () => (isMulti && Array.isArray(value) ? value : []),
        [isMulti, value],
    );

    const multiSelectedSet = useMemo(
        () => new Set(multiSelectedValues),
        [multiSelectedValues],
    );

    const filteredOptions = useMemo(() => {
        const term = String(searchText || '').trim().toLowerCase();
        if (!term) {
            return options;
        }
        return options.filter(opt => String(opt.label).toLowerCase().includes(term));
    }, [options, searchText]);

    const displayedOptions = useMemo(() => {
        if (!isMulti) {
            return filteredOptions;
        }

        const list = filteredOptions.slice();

        list.sort((a, b) => {
            const aLocked = lockedSet.has(a.value);
            const bLocked = lockedSet.has(b.value);

            if (aLocked && !bLocked) {
                return -1;
            }
            if (!aLocked && bLocked) {
                return 1;
            }

            const aSelected = multiSelectedSet.has(a.value);
            const bSelected = multiSelectedSet.has(b.value);

            if (aSelected === bSelected) return 0;
            return aSelected ? -1 : 1;
        });

        return list;
    }, [filteredOptions, isMulti, multiSelectedSet, lockedSet]);

    useEffect(() => {
        if (!isMulti) return;
        if (lockedSet.size === 0) return;

        const lockedFirst = Array.from(lockedSet)[0];
        if (lockedFirst && !multiSelectedValues.includes(lockedFirst)) {
            onChange?.([lockedFirst, ...multiSelectedValues]);
        }
    }, [isMulti, lockedSet, multiSelectedValues, onChange]);

    const selectedDisplayText = useMemo(() => {
        if (isMulti) {
            const labels = multiSelectedValues
                .map(v => rssdValueToLabelMap.get(v))
                .filter(Boolean);

            if (!labels.length) {
                return emptyText;
            }
            if (labels.length <= 2) {
                return labels.join(', ');
            }
            return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
        }

        const selectedValue = value || '';
        const label = selectedValue ? rssdValueToLabelMap.get(selectedValue) : '';
        return label || emptyText;
    }, [emptyText, isMulti, multiSelectedValues, rssdValueToLabelMap, value]);

    const handlePick = useCallback(
        nextValue => {
            if (isMulti) {
                if (lockedSet.has(nextValue)) {
                    return;
                }
                const updated = multiSelectedValues.includes(nextValue)
                    ? multiSelectedValues.filter(v => v !== nextValue)
                    : multiSelectedValues.concat(nextValue);

                onChange?.(updated);
                setSearchText('');
                return;
            }

            if (lockedSet.has(nextValue)) {
                return;
            }

            onChange?.(nextValue);
            setSearchText('');
            closeDropdown();
        },
        [closeDropdown, isMulti, multiSelectedValues, onChange, lockedSet],
    );

    const handleEscapeKey = useCallback(
        e => {
            if (e.key === 'Escape') {
                closeDropdown();
            }
        },
        [closeDropdown],
    );

    const isSelected = useCallback(
        optValue => {
            if (isMulti) {
                return multiSelectedSet.has(optValue);
            }
            return optValue === (value || '');
        },
        [isMulti, multiSelectedSet, value],
    );

    return (
        <div className="multiselect" ref={dropdownRef} onKeyDown={handleEscapeKey} role="presentation">
            <button
                id={id}
                type="button"
                className="multiselect-toggle"
                onClick={toggleDropdown}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-labelledby={ariaLabelledBy}
            >
                <span className="multiselect-toggle-text">{selectedDisplayText}</span>
                <span className={`multiselect-toggle-caret ${isOpen ? 'is-open' : ''}`} aria-hidden="true" />
            </button>

            {isOpen && (
                <div className="multiselect-panel" role="dialog" aria-label="RSSD dropdown">
                    <div className="multiselect-search">
                        <input
                            type="text"
                            value={searchText}
                            onChange={handleSearchChange}
                            placeholder={placeholder}
                            className="multiselect-search-input"
                            aria-label="Search options"
                        />
                    </div>

                    <div className="multiselect-group-title">{groupTitle}</div>

                    <div className="multiselect-list" role="listbox" aria-multiselectable={isMulti}>
                        {displayedOptions.map(opt => {
                            const isLocked = lockedSet.has(opt.value);
                            return (
                                <div
                                    key={opt.value}
                                    className={`multiselect-row ${isLocked ? 'locked' : ''}`}
                                    role="presentation"
                                    onClick={() => !isLocked && handlePick(opt.value)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected(opt.value)}
                                        onChange={() => !isLocked && handlePick(opt.value)}
                                        onClick={e => e.stopPropagation()}
                                        disabled={isLocked}
                                        aria-label={`${isMulti ? 'Toggle' : 'Select'} ${opt.label}${isLocked ? ' (locked)' : ''}`}
                                    />
                                    <span className="multiselect-row-text">
                                        {opt.label}
                                        {isLocked ? ' (Locked - Change in Variations Tab)' : ''}
                                    </span>
                                </div>
                            );
                        })}

                        {!displayedOptions.length ? <div className="multiselect-empty">No matches</div> : null}
                    </div>
                </div>
            )}
        </div>
    );
};

const SingleSelect = ({
    id,
    labelId,
    value,
    options,
    onValueChange,
}) => {
    const handleSelect = useCallback(
        selected => {
            const isObj = selected && typeof selected === 'object' && 'value' in selected;
            const nextValue = isObj ? selected.value : selected;
            onValueChange?.(nextValue);
        },
        [onValueChange],
    );

    const normalizedValue = value === '' ? undefined : value;

    return (
        <Select
            id={id}
            value={normalizedValue}
            onSelect={handleSelect}
            aria-labelledby={labelId}
            className="header-select"
        >
            {options.map(opt => (
                <SelectOption
                    key={opt.value}
                    value={opt.value}
                    selectedDisplay={opt.selectedDisplay || opt.label}
                >
                    {opt.node || opt.label}
                </SelectOption>
            ))}
        </Select>
    );
};

const Header = ({
    reportingForm = '',
    startQuarter = '',
    endQuarter = '',
    formsList = [],
    rssdList = [],
    startQuarterList = [],
    endQuarterList = [],
    onFormChange,
    onInstitutionChange,
    onStartQuarterChange,
    onEndQuarterChange,
    onBenchmarkQuarterChange,
    onBenchmarkRSSDChange,
    reportType,
    benchmarkRSSDs = [],
    onStarClick,
}) => {
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [selectedInstitutionRssd, setSelectedInstitutionRssd] = useState('');
    const [selectedBenchmarkRssds, setSelectedBenchmarkRssds] = useState([]);
    const [benchmarkQuarter, setBenchmarkQuarter] = useState('');

    const benchmarkDefaultsAppliedRef = useRef(false);

    useEffect(() => {
        const options = rssdList.map(item => {
            const parts = String(item).split(' - ');
            return { value: parts[0], label: item };
        });
        setInstitutionOptions(options);
    }, [rssdList]);

    useEffect(() => {
        const next = Array.isArray(benchmarkRSSDs) ? benchmarkRSSDs : [];
        setSelectedBenchmarkRssds(next);

        if (next.length) {
            benchmarkDefaultsAppliedRef.current = true;
        }
    }, [benchmarkRSSDs]);

    useEffect(() => {
        if (reportType === BENCHMARK) {
            if (!benchmarkQuarter) {
                const defaultQuarter = getDefaultBenchmarkQuarter(startQuarterList, endQuarterList);
                if (defaultQuarter) {
                    setBenchmarkQuarter(defaultQuarter);
                    onBenchmarkQuarterChange?.(defaultQuarter);
                }
            }

            if (!selectedInstitutionRssd && institutionOptions.length) {
                const locked = institutionOptions[0].value;
                setSelectedInstitutionRssd(locked);
                onInstitutionChange?.(locked);
            }

            if (
                (!selectedBenchmarkRssds || selectedBenchmarkRssds.length === 0) &&
                institutionOptions.length > 1
            ) {
                const locked = selectedInstitutionRssd || institutionOptions[0].value;
                const secondVal = institutionOptions.find(val => val.value !== locked)?.value || null;

                const initialBenchmark = secondVal ? [locked, secondVal] : [locked];
                setSelectedBenchmarkRssds(initialBenchmark);
                onBenchmarkRSSDChange?.(initialBenchmark);
            }

            return;
        }

        if (!selectedInstitutionRssd && institutionOptions.length) {
            const locked = institutionOptions[0].value;
            setSelectedInstitutionRssd(locked);
            onInstitutionChange?.(locked);

            if (
                (!selectedBenchmarkRssds || selectedBenchmarkRssds.length === 0) &&
                institutionOptions.length > 1
            ) {
                const secondVal = institutionOptions[1].value;
                const initialBenchmark = [locked, secondVal];
                setSelectedBenchmarkRssds(initialBenchmark);
                onBenchmarkRSSDChange?.(initialBenchmark);
            }
        }

        if (!startQuarter && startQuarterList.length) {
            onStartQuarterChange?.(startQuarterList[0]);
        }

        if (!endQuarter && endQuarterList.length) {
            onEndQuarterChange?.(endQuarterList[endQuarterList.length - 1]);
        }
    }, [
        benchmarkQuarter,
        endQuarter,
        endQuarterList,
        institutionOptions,
        onBenchmarkQuarterChange,
        onBenchmarkRSSDChange,
        onEndQuarterChange,
        onInstitutionChange,
        onStartQuarterChange,
        reportType,
        selectedInstitutionRssd,
        startQuarter,
        startQuarterList,
        selectedBenchmarkRssds,
    ]);

    const formatQuarterLabel = useCallback(q => String(q).replace('_', ' ').toUpperCase(), []);

    const reportingFormOptions = useMemo(
        () => formsList.map(form => ({ value: form, label: form, selectedDisplay: form })),
        [formsList],
    );

    const startQuarterOptions = useMemo(
        () => startQuarterList.map(q => {
            const label = formatQuarterLabel(q);
            return { value: q, label, selectedDisplay: label };
        }),
        [formatQuarterLabel, startQuarterList],
    );

    const endQuarterOptions = useMemo(
        () => endQuarterList.map(q => {
            const label = formatQuarterLabel(q);
            return { value: q, label, selectedDisplay: label };
        }),
        [endQuarterList, formatQuarterLabel],
    );

    const handleInstitutionChange = useCallback(
        nextValue => {
            const locked = nextValue || '';
            setSelectedInstitutionRssd(locked);
            onInstitutionChange?.(locked);

            if (!institutionOptions || institutionOptions.length === 0) {
                setSelectedBenchmarkRssds([locked]);
                onBenchmarkRSSDChange?.([locked]);
                return;
            }

            const firstVal = institutionOptions[0].value;
            const secondValInList = institutionOptions.length > 1 ? institutionOptions[1].value : null;

            let secondVal;

            if (locked === firstVal && secondValInList) {
                secondVal = secondValInList;
            } else if (locked === secondValInList && firstVal) {
                secondVal = firstVal;
            } else if (firstVal !== locked) {
                secondVal = firstVal;
            } else {
                secondVal = null;
            }

            const newBenchmark = secondVal ? [locked, secondVal] : [locked];

            setSelectedBenchmarkRssds(newBenchmark);
            onBenchmarkRSSDChange?.(newBenchmark);
        },
        [institutionOptions, onInstitutionChange, onBenchmarkRSSDChange],
    );

    const handleBenchmarkQuarterSelect = useCallback(
        val => {
            setBenchmarkQuarter(val);
            onBenchmarkQuarterChange?.(val);
        },
        [onBenchmarkQuarterChange],
    );

    const handleBenchmarkRssdsChange = useCallback(
        updated => {
            const next = Array.isArray(updated) ? updated : [];
            benchmarkDefaultsAppliedRef.current = true;
            setSelectedBenchmarkRssds(next);
            onBenchmarkRSSDChange?.(next);
        },
        [onBenchmarkRSSDChange],
    );

    const handleStarClick = useCallback(() => {
        onStarClick?.({
            rssid: selectedInstitutionRssd || '',
            startQuarter,
            endQuarter,
            reportingForm,
        });
    }, [endQuarter, onStarClick, reportingForm, selectedInstitutionRssd, startQuarter]);

    const rowClassName = useMemo(
        () => (reportType === BENCHMARK ? 'header-row header-row-benchmark' : 'header-row header-row-variations'),
        [reportType],
    );

    const sortedInstitutionOptions = useMemo(() => {
        if (!selectedInstitutionRssd) {
            return institutionOptions;
        }

        const selectedOption = institutionOptions.find(opt => opt.value === selectedInstitutionRssd);

        if (!selectedOption) {
            return institutionOptions;
        }

        return [selectedOption, ...institutionOptions.filter(opt => opt.value !== selectedInstitutionRssd)];
    }, [institutionOptions, selectedInstitutionRssd]);

    return (
        <div className="header-full">
            <div className="header-container">
                <div className={rowClassName}>
                    <div className="field">
                        <div id="reporting-form-label" className="field-label">Reporting Form</div>
                        <SingleSelect
                            id="reporting-form"
                            labelId="reporting-form-label"
                            value={reportingForm}
                            options={reportingFormOptions}
                            onValueChange={val => onFormChange?.(val)}
                        />
                    </div>

                    {reportType !== BENCHMARK ? (
                        <div className="field rssd-field">
                            <div id="institution-label" className="field-label">RSSD ID / Institution Name</div>

                            <div className="rssd-actions">
                                <RssdDropdown
                                    id="institution-rssd-dropdown"
                                    labelId="institution-label"
                                    options={sortedInstitutionOptions}
                                    value={selectedInstitutionRssd}
                                    onChange={handleInstitutionChange}
                                    placeholder="Search RSSD ID or Name"
                                    groupTitle={GROUP_TITLE}
                                    emptyText="Click to select Institution"
                                />

                                <button
                                    type="button"
                                    className="animated-star"
                                    title={TITLE_LABEL}
                                    onClick={handleStarClick}
                                    aria-label="Generate RSSID Narrative"
                                >
                                    ✨
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {reportType !== BENCHMARK ? (
                        <div className="field">
                            <div id="start-quarter-label" className="field-label">Start Quarter</div>
                            <SingleSelect
                                id="start-quarter"
                                labelId="start-quarter-label"
                                value={startQuarter}
                                options={startQuarterOptions}
                                onValueChange={val => onStartQuarterChange?.(val)}
                            />
                        </div>
                    ) : null}

                    {reportType !== BENCHMARK ? (
                        <div className="field">
                            <div id="end-quarter-label" className="field-label">End Quarter</div>
                            <SingleSelect
                                id="end-quarter"
                                labelId="end-quarter-label"
                                value={endQuarter}
                                options={endQuarterOptions}
                                onValueChange={val => onEndQuarterChange?.(val)}
                            />
                        </div>
                    ) : null}

                    {reportType === BENCHMARK ? (
                        <>
                            <div className="field">
                                <div id="benchmark-quarter-label" className="field-label">Benchmark Quarter</div>
                                <SingleSelect
                                    id="benchmark-quarter"
                                    labelId="benchmark-quarter-label"
                                    value={benchmarkQuarter}
                                    options={startQuarterOptions}
                                    onValueChange={handleBenchmarkQuarterSelect}
                                />
                            </div>

                            <div className="field benchmark-compare">
                                <div id="benchmark-rssds-label" className="field-label">Compare With RSSD IDs</div>
                                <RssdDropdown
                                    id="benchmark-rssd-dropdown"
                                    labelId="benchmark-rssds-label"
                                    options={institutionOptions}
                                    value={selectedBenchmarkRssds}
                                    onChange={handleBenchmarkRssdsChange}
                                    placeholder={PLACEHOLDER}
                                    groupTitle={GROUP_TITLE}
                                    emptyText={EMPTY_TEXT}
                                    isMulti
                                    lockedValues={selectedInstitutionRssd ? [selectedInstitutionRssd] : []}
                                />
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default Header;
